import React from 'react';
import PropTypes from 'prop-types';
import { driverRecordShape } from './driverRecordShape';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import popupCardBase from './popupCardStyles';
import { STATUS_TIER_LABEL, accentClassVariants } from './statusStyles';
import {
  formatDeltaLabel,
  formatLastPingSeconds,
  formatDriverNames,
} from '../../../utils/displayFormat';
import Button from '../../../components/atoms/forms/Button';

// Driver-detail card shown in the map marker's Leaflet popup. Card styles are
// shared with StopPopupContent via popupCardBase; self-contained (dpc-*) so it
// renders correctly wherever it's mounted.
const StyledCard = styledComponent('div', {
  ...popupCardBase('dpc'),
  '& .dpc-meta': {
    fontSize: theme.sizes.captionFontSize,
    color: theme.colors.textMuted,
    marginBottom: '8px',
  },
  '& .dpc-status': {
    color: theme.colors.statusNeutral,
  },
  ...accentClassVariants('dpc-status', 'color'),
  '& .dpc-actions': {
    marginTop: '8px',
    paddingTop: '8px',
    borderTop: `1px solid ${theme.colors.borderLight}`,
    display: 'flex',
    justifyContent: 'flex-end',
  },
});

function DriverPopupContent({ driver, onViewRoute }) {
  const etaText = formatDeltaLabel(driver.etaDeltaMin, {
    unit: 'min',
    zeroLabel: null,
  });
  return (
    <StyledCard>
      <div className="dpc-name">{formatDriverNames(driver.driverNames)}</div>
      <div className="dpc-meta">
        {driver.routeName}
        {driver.vehicleName ? ` · ${driver.vehicleName}` : ''}
      </div>
      <div className="dpc-row">
        <span className="dpc-label">Status</span>
        <span className={`dpc-value dpc-status ${driver.status}`}>
          {STATUS_TIER_LABEL[driver.status] || driver.status}
        </span>
      </div>
      <div className="dpc-row">
        <span className="dpc-label">ETA delta</span>
        <span className="dpc-value">{etaText}</span>
      </div>
      {driver.trafficDelayTodayMin !== null &&
        driver.trafficDelayTodayMin !== undefined && (
          <div className="dpc-row">
            <span className="dpc-label">Traffic delay today</span>
            <span className="dpc-value">
              {formatDeltaLabel(driver.trafficDelayTodayMin, {
                unit: 'min',
                zeroLabel: 'none',
              })}
            </span>
          </div>
        )}
      <div className="dpc-row">
        <span className="dpc-label">Progress</span>
        <span className="dpc-value">
          {`${driver.stopsCompleted} / ${driver.stopsTotal}`}
        </span>
      </div>
      <div className="dpc-row">
        <span className="dpc-label">Last ping</span>
        <span className="dpc-value">
          {formatLastPingSeconds(driver.lastPingSecondsAgo, {
            suffix: ' ago',
          }) ?? '—'}
        </span>
      </div>
      {onViewRoute && (
        <div className="dpc-actions">
          <Button
            variant="link-primary"
            onClick={() => onViewRoute(driver.routeId)}
            title="Open this route on the map"
          >
            View route
          </Button>
        </div>
      )}
    </StyledCard>
  );
}

DriverPopupContent.propTypes = {
  driver: driverRecordShape.isRequired,
  onViewRoute: PropTypes.func,
};

DriverPopupContent.defaultProps = {
  onViewRoute: null,
};

export default DriverPopupContent;
